import type { ReactNode } from "react"
import type { DateRange } from "react-day-picker"

import type { SerializedDateRange } from "../../../typings"
import {
	DateAfterFilterModifier,
	DateBeforeFilterModifier,
	DateRangeInFilterModifier,
	DateRangeNotInFilterModifier,
	NullableDateEqualsFilterModifier,
	NullableDateNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { DueDateFilterCell } from "./DueDateFilterCell"

type DueDateFieldValue = Date | null
type DueDateFilterValue = Date | DateRange | null
type SerializedDueDateFilterValue = string | SerializedDateRange | null

export class DueDateFilterDef extends BaseFilterDef<
	DueDateFieldValue,
	DueDateFilterValue,
	SerializedDueDateFilterValue
> {
	readonly id = "due_date"
	readonly defaultFilterValue: DueDateFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableDateEqualsFilterModifier,
			isValueValid: (value: DueDateFieldValue): value is Date | null => value !== null,
			isFilterValueValid: (filterValue: DueDateFilterValue): filterValue is Date | null =>
				filterValue instanceof Date || filterValue === null,
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableDateNotEqualsFilterModifier,
			isValueValid: (value: DueDateFieldValue): value is Date | null => value !== null,
			isFilterValueValid: (filterValue: DueDateFilterValue): filterValue is Date | null =>
				filterValue instanceof Date || filterValue === null,
		}),
		before: createFilterModifierConfig({
			modifier: DateBeforeFilterModifier,
			isValueValid: (value: DueDateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DueDateFilterValue): filterValue is Date => filterValue instanceof Date,
		}),
		after: createFilterModifierConfig({
			modifier: DateAfterFilterModifier,
			isValueValid: (value: DueDateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DueDateFilterValue): filterValue is Date => filterValue instanceof Date,
		}),
		in: createFilterModifierConfig({
			modifier: DateRangeInFilterModifier,
			isValueValid: (value: DueDateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DueDateFilterValue): filterValue is DateRange =>
				"to" in (filterValue ?? {}) && "from" in (filterValue ?? {}),
		}),
		notIn: createFilterModifierConfig({
			modifier: DateRangeNotInFilterModifier,
			isValueValid: (value: DueDateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DueDateFilterValue): filterValue is DateRange =>
				"to" in (filterValue ?? {}) && "from" in (filterValue ?? {}),
		}),
	}

	render(props: FilterDefRenderProps<DueDateFilterDef>): ReactNode {
		return <DueDateFilterCell key={props.filter.id} {...props} />
	}
}
